import { useState } from "react";
import { useTranslation } from "react-i18next";

import { TextArea } from "../../components/Form/Input/textArea";
import Label from "../../components/Form/Label";
import { FormGroup } from "../../components/Form/styles";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { MAX_SUGGESTION_LENGTH, isTooManySuggestions } from "../../services/suggestions";

const prefix = "pages.releases.suggestion";

type Props = {
  /** Sends the text; a rejection keeps the modal open with the text and an error. */
  onSend: (message: string) => Promise<void>;
  onClose: () => void;
};

/**
 * "Sugira um recurso ou ideia": a text field, then thanks. Closed only by the X, Escape or Cancel (a click outside would lose
 * what was written); nothing closes it while sending.
 */
const SuggestionModal = ({ onSend, onClose }: Props) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<"generic" | "tooMany" | null>(null);
  const [isSent, setSent] = useState(false);

  const text = message.trim();

  const send = () => {
    if (!text) return;
    setSending(true);
    setError(null);
    onSend(text)
      .then(() => setSent(true))
      .catch(e => setError(isTooManySuggestions(e) ? "tooMany" : "generic"))
      .finally(() => setSending(false));
  };

  if (isSent) {
    // its own key: a new modal, so the focus goes into it (the field that had it is gone)
    return (
      <Modal
        key="thanks"
        size="sm"
        onExit={onClose}
        outsideExit
        title={t(`${prefix}.thanks.title`)}
        subtitle={t(`${prefix}.thanks.description`)}
        footer={<ModalButton onClick={onClose}>{t(`${prefix}.thanks.close`)}</ModalButton>}
      />
    );
  }

  return (
    <Modal
      key="form"
      onExit={onClose}
      escapeExit
      busy={sending}
      title={t(`${prefix}.title`)}
      subtitle={t(`${prefix}.subtitle`)}
      error={error ? t(`${prefix}.${error === "tooMany" ? "tooMany" : "error"}`) : undefined}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onClose} disabled={sending}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          <ModalButton onClick={send} disabled={!text} loading={sending}>
            {t(`${prefix}.send`)}
          </ModalButton>
        </>
      }>
      <FormGroup>
        <TextArea
          id="suggestion"
          name="suggestion"
          autoComplete="off"
          autoFocus
          rows={6}
          maxLength={MAX_SUGGESTION_LENGTH}
          value={message}
          onChange={e => {
            setMessage(e.target.value);
            setError(null);
          }}
        />
        <Label
          htmlFor="suggestion"
          text={t(`${prefix}.label`)}
          maxLength={MAX_SUGGESTION_LENGTH}
          usedLength={message.length}
        />
      </FormGroup>
    </Modal>
  );
};

export default SuggestionModal;
